<script setup>
import { Head, Link } from '@inertiajs/vue3';
import AppLayout from '@/Layouts/AppLayout.vue';
import { isBrandDirect as checkBrandDirect } from '@/lib/brand';
import LeadFunnel from '@/Components/LeadFunnel.vue';
import { useCopy } from '@/composables/useCopy';

const props = defineProps({
    domain: Object,
    service: String,
    branding: Object,
    designVariant: Object,
    copyPack: { type: Object, default: () => ({}) },
    spotImages: { type: Object, default: () => ({}) },
});

const { t, ts, spot } = useCopy(props);

const isBrandDirect = checkBrandDirect(props.domain);
const brandCta = props.domain?.settings?.brand_cta || 'Jetzt beraten lassen';
const serviceLabel = props.service === 'wp' ? 'Wärmepumpen' : 'Photovoltaik';
const serviceSingular = props.service === 'wp' ? 'Wärmepumpe' : 'Solaranlage';

const steps = [
    { num: t('how_step_1_num', '01'), title: ts('how_step_1_title', 'Beratung starten'), desc: ts('how_step_1_desc', 'Sag uns in 2 Minuten, was du brauchst. Unser Online-Fragebogen ist super einfach - kein Fachwissen nötig.') },
    { num: t('how_step_2_num', '02'), title: ts('how_step_2_title', 'Analyse & Planung'), desc: ts('how_step_2_desc', 'Unsere Experten checken deine Angaben und erstellen ein individuelles Konzept für dein Projekt.') },
    { num: t('how_step_3_num', '03'), title: ts('how_step_3_title', 'Angebote vergleichen'), desc: ts('how_step_3_desc', 'Du erhältst bis zu 3 Angebote von regionalen Fachbetrieben aus deiner Region - transparent und kostenlos.') },
    { num: t('how_step_4_num', '04'), title: ts('how_step_4_title', 'Installation'), desc: ts('how_step_4_desc', 'Dein ausgewählter Fachbetrieb installiert deine Anlage termingerecht - inklusive Inbetriebnahme.') },
];

const heroImage = props.spotImages?.welcome_hero || null;
const stepSpots = ['how_step_1', 'how_step_2', 'how_step_3', 'how_step_4'];
</script>

<template>
    <Head :title="`So funktioniert's - Dein Weg zur ${serviceSingular}`">
        <meta name="description" :content="`Erfahre, wie du in 4 einfachen Schritten zu deiner neuen ${serviceSingular} kommst: Beratung, Planung, Angebotsvergleich und Installation.`" />
        <meta property="og:title" :content="`So funktioniert's - Dein Weg zur ${serviceSingular}`" />
        <meta property="og:type" content="website" />
    </Head>

    <AppLayout :domain="domain" :branding="branding" :designVariant="designVariant" :service="service" activePage="how" heroMode>
        <!-- Hero -->
        <section class="sv-hero" aria-label="So funktioniert es">
            <div class="sv-hero__bg" aria-hidden="true"></div>
            <div class="sv-hero__content">
                <span class="sv-hero__tag">{{ t('how_hero_tag', `So funktioniert's`) }}</span>
                <h1 class="sv-hero__title">{{ ts('how_hero_title', `Dein Weg zur neuen ${serviceSingular}.`) }}</h1>
                <p class="sv-hero__sub">{{ t('how_hero_sub', 'Wir begleiten dich Schritt für Schritt - von der ersten Anfrage bis zum fertigen Angebot. Einfach, transparent, stressfrei.') }}</p>
            </div>
        </section>

        <!-- Trust bar -->
        <div class="sv-trust-bar">
            <div class="sv-trust-bar__inner">
                <span class="sv-trust-bar__item">{{ t('how_trust_1', '4 einfache Schritte') }}</span>
                <span class="sv-trust-bar__sep">|</span>
                <span class="sv-trust-bar__item">{{ t('how_trust_2', '100 % kostenlos') }}</span>
                <span class="sv-trust-bar__sep">|</span>
                <span class="sv-trust-bar__item">{{ t('how_trust_3', 'Unverbindlich') }}</span>
            </div>
        </div>

        <!-- Steps -->
        <section class="sv-steps-section" aria-label="Schritte">
            <div class="sv-steps-section__inner">
                <h2 class="sv-steps-section__heading">{{ t('how_steps_heading', 'In 4 Schritten zum Ziel') }}</h2>
                <p class="sv-steps-section__sub">{{ t('how_steps_sub', 'Von der ersten Anfrage bis zur fertigen Installation - so einfach geht es.') }}</p>

                <div class="sv-steps-grid">
                    <div v-for="(step, idx) in steps" :key="step.num" class="sv-step-card">
                        <div class="sv-step-card__num">{{ step.num }}</div>
                        <h3 class="sv-step-card__title">{{ step.title }}</h3>
                        <p class="sv-step-card__desc">{{ step.desc }}</p>
                        <div class="sv-step-card__progress">
                            <div
                                v-for="dot in 4"
                                :key="dot"
                                class="sv-step-card__dot"
                                :class="{ 'sv-step-card__dot--active': dot <= idx + 1 }"
                            ></div>
                        </div>
                    </div>
                </div>
            </div>
        </section>

        <!-- Quality -->
        <section class="sv-quality-section" aria-label="Qualitätsversprechen">
            <div class="sv-quality-section__inner sv-quality-section__inner--centered">
                <h2 class="sv-quality-section__heading">{{ t('how_quality_heading', 'Qualität, der du vertrauen kannst') }}</h2>
                <p class="sv-quality-section__sub">{{ t('how_quality_sub', 'Wir arbeiten nur mit sorgfältig ausgewählten Partnerbetrieben zusammen.') }}</p>
                <ul class="sv-quality-list sv-quality-list--grid">
                    <li class="sv-quality-list__item">
                        <span class="sv-quality-list__check">{{ t('how_quality_check_glyph', '[ok]') }}</span>
                        {{ t('how_quality_item_1', 'Regionale Fachbetriebe') }}
                    </li>
                    <li class="sv-quality-list__item">
                        <span class="sv-quality-list__check">{{ t('how_quality_check_glyph', '[ok]') }}</span>
                        {{ t('how_quality_item_2', 'Hochwertige Premium-Komponenten') }}
                    </li>
                    <li class="sv-quality-list__item">
                        <span class="sv-quality-list__check">{{ t('how_quality_check_glyph', '[ok]') }}</span>
                        {{ t('how_quality_item_3', 'Persönlicher Ansprechpartner') }}
                    </li>
                </ul>
            </div>
        </section>

        <!-- CTA -->
        <section class="sv-cta-section" aria-label="Jetzt starten">
            <div class="sv-cta-section__inner">
                <h2 class="sv-cta-section__heading">{{ t('how_cta_heading', 'Bereit für den nächsten Schritt?') }}</h2>
                <p class="sv-cta-section__sub">{{ isBrandDirect ? t('how_cta_sub_brand', 'Fordere jetzt dein kostenloses Angebot an und spare bares Geld.') : t('how_cta_sub', 'Starte jetzt deinen kostenlosen Vergleich und spare bares Geld.') }}</p>
                <div class="sv-cta-section__funnel">
                    <div class="sv-funnel-wrap">
                        <LeadFunnel :domain="domain" :service="service" :design-variant="designVariant" bare :dark-mode="false" />
                    </div>
                </div>
            </div>
        </section>
    </AppLayout>
</template>

<style scoped>
/* ===== HERO ===== */
.sv-hero {
    position: relative;
    min-height: 420px;
    display: flex;
    align-items: center;
    overflow: hidden;
    background: #0a1628;
}
.sv-hero__bg {
    position: absolute;
    inset: 0;
    background: linear-gradient(135deg, #0a1628 0%, #001d75 100%);
    opacity: 0.95;
}
.sv-hero__content {
    position: relative;
    z-index: 2;
    max-width: 720px;
    padding: 100px 24px 60px;
    margin: 0 auto 0 0;
}
@media (min-width: 768px) {
    .sv-hero__content { padding: 120px 48px 70px; margin-left: max(calc((100% - 1200px) / 2), 48px); }
}
.sv-hero__tag {
    display: inline-block;
    padding: 6px 18px;
    border-radius: 20px;
    font-family: 'Inter', sans-serif;
    font-size: 12px;
    font-weight: 700;
    letter-spacing: 1.5px;
    text-transform: uppercase;
    color: #00d782;
    border: 1px solid rgba(0, 215, 130, 0.3);
    background: rgba(0, 215, 130, 0.08);
    margin-bottom: 20px;
}
.sv-hero__title {
    font-family: 'Inter', sans-serif;
    font-size: clamp(32px, 5vw, 52px);
    font-weight: 800;
    color: #ffffff;
    line-height: 1.1;
    margin-bottom: 16px;
}
.sv-hero__sub {
    font-family: 'Inter', sans-serif;
    font-size: 17px;
    line-height: 1.7;
    color: rgba(255, 255, 255, 0.7);
    max-width: 540px;
}

/* ===== TRUST BAR ===== */
.sv-trust-bar {
    background: #0f1d32;
    border-top: 1px solid rgba(0, 215, 130, 0.15);
    padding: 14px 24px;
}
.sv-trust-bar__inner {
    max-width: 900px;
    margin: 0 auto;
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 12px;
    flex-wrap: wrap;
}
.sv-trust-bar__item {
    font-family: 'Inter', sans-serif;
    font-size: 13px;
    font-weight: 600;
    color: #00d782;
}
.sv-trust-bar__sep {
    color: rgba(255, 255, 255, 0.15);
    font-size: 12px;
}

/* ===== STEPS SECTION ===== */
.sv-steps-section {
    padding: 80px 0;
    background: #f0f4f8;
}
@media (min-width: 768px) { .sv-steps-section { padding: 100px 0; } }
.sv-steps-section__inner {
    max-width: 1100px;
    margin: 0 auto;
    padding: 0 24px;
}
.sv-steps-section__heading {
    font-family: 'Inter', sans-serif;
    font-size: clamp(24px, 3vw, 36px);
    font-weight: 800;
    color: #1a1a1a;
    text-align: center;
    margin-bottom: 12px;
}
.sv-steps-section__sub {
    font-family: 'Inter', sans-serif;
    font-size: 16px;
    color: #64748b;
    text-align: center;
    margin-bottom: 48px;
}
.sv-steps-grid {
    display: grid;
    grid-template-columns: 1fr;
    gap: 24px;
}
@media (min-width: 640px) { .sv-steps-grid { grid-template-columns: repeat(2, 1fr); } }
@media (min-width: 1024px) { .sv-steps-grid { grid-template-columns: repeat(4, 1fr); } }
.sv-step-card {
    background: #0f1d32;
    border-radius: 16px;
    padding: 32px 24px;
    position: relative;
    overflow: hidden;
    transition: transform 250ms ease, box-shadow 250ms ease;
}
.sv-step-card::before {
    content: '';
    position: absolute;
    inset: 0;
    border-radius: 16px;
    padding: 2px;
    background: linear-gradient(135deg, transparent, transparent);
    -webkit-mask: linear-gradient(#fff 0 0) content-box, linear-gradient(#fff 0 0);
    mask: linear-gradient(#fff 0 0) content-box, linear-gradient(#fff 0 0);
    -webkit-mask-composite: xor;
    mask-composite: exclude;
    pointer-events: none;
    transition: background 300ms ease;
}
.sv-step-card:hover::before {
    background: linear-gradient(135deg, #00d782, #ffd700);
}
.sv-step-card:hover {
    transform: translateY(-4px);
    box-shadow: 0 12px 40px rgba(0, 215, 130, 0.15);
}
.sv-step-card__num {
    font-family: 'Inter', sans-serif;
    font-size: 42px;
    font-weight: 800;
    color: #00d782;
    line-height: 1;
    margin-bottom: 16px;
}
.sv-step-card__title {
    font-family: 'Inter', sans-serif;
    font-size: 18px;
    font-weight: 700;
    color: #ffffff;
    margin-bottom: 10px;
}
.sv-step-card__desc {
    font-family: 'Inter', sans-serif;
    font-size: 14px;
    color: rgba(255, 255, 255, 0.6);
    line-height: 1.65;
}
.sv-step-card__progress {
    display: flex;
    gap: 6px;
    margin-top: 20px;
}
.sv-step-card__dot {
    width: 8px;
    height: 8px;
    border-radius: 50%;
    background: rgba(255, 255, 255, 0.15);
    transition: background 200ms ease;
}
.sv-step-card__dot--active {
    background: #00d782;
}

/* ===== QUALITY ===== */
.sv-quality-section {
    padding: 80px 0;
    background: #ffffff;
}
@media (min-width: 768px) { .sv-quality-section { padding: 100px 0; } }
.sv-quality-section__inner {
    max-width: 1100px;
    margin: 0 auto;
    padding: 0 24px;
    display: flex;
    flex-direction: column;
    gap: 40px;
}
@media (min-width: 1024px) { .sv-quality-section__inner { flex-direction: row; gap: 48px; align-items: center; } }
.sv-quality-section__text { flex: 1; }
.sv-quality-section__heading {
    font-family: 'Inter', sans-serif;
    font-size: clamp(24px, 3vw, 32px);
    font-weight: 800;
    color: #1a1a1a;
    margin-bottom: 12px;
}
.sv-quality-section__sub {
    font-family: 'Inter', sans-serif;
    font-size: 15px;
    color: #64748b;
    line-height: 1.6;
    margin-bottom: 24px;
}
.sv-quality-list {
    list-style: none;
    padding: 0;
    margin: 0;
    display: flex;
    flex-direction: column;
    gap: 14px;
}
.sv-quality-list__item {
    display: flex;
    align-items: center;
    gap: 12px;
    font-family: 'Inter', sans-serif;
    font-size: 15px;
    color: #1a1a1a;
    font-weight: 500;
}
.sv-quality-list__check {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 28px;
    height: 28px;
    border-radius: 8px;
    background: rgba(0, 215, 130, 0.1);
    color: #00d782;
    font-size: 11px;
    font-weight: 700;
    flex-shrink: 0;
}
.sv-quality-section__funnel {
    width: 100%;
    flex-shrink: 0;
}
@media (min-width: 1024px) { .sv-quality-section__funnel { width: 480px; } }

/* ===== CTA ===== */
.sv-cta-section {
    padding: 80px 0;
    background: #0a1628;
}
@media (min-width: 768px) { .sv-cta-section { padding: 100px 0; } }
.sv-cta-section__inner {
    max-width: 700px;
    margin: 0 auto;
    padding: 0 24px;
    text-align: center;
}
.sv-cta-section__heading {
    font-family: 'Inter', sans-serif;
    font-size: clamp(24px, 3vw, 36px);
    font-weight: 800;
    color: #ffffff;
    margin-bottom: 12px;
}
.sv-cta-section__sub {
    font-family: 'Inter', sans-serif;
    font-size: 16px;
    color: rgba(255, 255, 255, 0.65);
    line-height: 1.7;
    margin-bottom: 32px;
}
.sv-cta-section__funnel {
    max-width: 520px;
    margin: 0 auto;
}

/* ===== SHARED FUNNEL WRAP ===== */
.sv-funnel-wrap {
    padding: 24px;
    background: #ffffff;
    border: 1px solid #e2e8f0;
    border-radius: 16px;
    box-shadow: 0 8px 32px rgba(0, 0, 0, 0.08);
}
</style>
